// LILIPRO PET — Videos comprables (sección lp-shoppable-videos).
// Fuente: py/theme_pet/videos_home/lp-shoppable-videos.js — se sube con py/theme_pet/videos_home_pet.py.
// - Carga: solo pósters. El <video> no tiene src hasta que su tarjeta está en pantalla.
// - Vista previa: en silencio, UNA a la vez (la tarjeta visible), N segundos y pasa a la siguiente visible.
//   Con ahorro de datos, red 2g/3g o "reducir movimiento" no arranca sola.
// - Al tocar: visor tipo historias con sonido (HLS si el navegador lo trae nativo, si no mp4 720p).
// - GA4: lp_video_view / lp_video_open / lp_video_click (nombres propios, no tocan add_to_cart nativo).

const SLOW = (() => {
  const c = navigator.connection;
  return !!(c && (c.saveData || /(^|-)(2g|3g)$/.test(c.effectiveType || '')));
})();
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;

const ICON_MUTED = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4z"/><path d="m23 9-6 6"/><path d="m17 9 6 6"/></svg>';
const ICON_SOUND = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M19 5a10 10 0 0 1 0 14"/></svg>';
const ICON_CLOSE = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>';
const ICON_PAUSE = '<svg viewBox="0 0 24 24" width="26" height="26" fill="#fff"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z"/></svg>';
const chevron = (d) => `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="${d}"/></svg>`;

// Liquid solo expone el mp4 1080p (hasta 7 Mbps). Los mp4 480p/720p existen en el CDN con el
// mismo nombre que su variante del playlist HLS: "<id>.SD-480p.0.9Mbps.hls.<n>_.m3u8" → "<id>.SD-480p-0.9Mbps-<n>.mp4".
const RENDITIONS = new Map();
function renditions(hls) {
  if (!hls) return Promise.resolve({});
  if (!RENDITIONS.has(hls)) {
    const base = hls.split('?')[0].replace(/[^/]+$/, '');
    RENDITIONS.set(hls, fetch(hls)
      .then((r) => (r.ok ? r.text() : ''))
      .then((txt) => {
        const out = [];
        const lines = txt.split('\n');
        lines.forEach((line, i) => {
          const res = /RESOLUTION=\d+x(\d+)/.exec(line);
          const name = /^(.+?)\.(SD|HD)-(\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/.exec((lines[i + 1] || '').trim());
          if (res && name) out.push({ h: +res[1], url: `${base}${name[1]}.${name[2]}-${name[3]}-${name[4]}-${name[5]}.mp4` });
        });
        out.sort((a, b) => a.h - b.h);
        return { low: out[0]?.url, mid: (out.find((o) => o.h >= 720) || out[out.length - 1])?.url };
      })
      .catch(() => ({})));
  }
  return RENDITIONS.get(hls);
}

function track(name, params) {
  try {
    const p = Object.assign({ bloque: 'home_videos', currency: 'CLP' }, params);
    if (typeof window.gtag === 'function') window.gtag('event', name, p);
    else (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, p));
  } catch (e) {}
}

class LpShoppableVideos extends HTMLElement {
  // LP-POSTER-DEFER (14-oct-2026): las portadas esperan a que termine la imagen LCP del hero
  // para no quitarle ancho de banda (py/theme_pet/performance_home_pet.py).
  deferPosters() {
    (function (root) {
      if (!root) return;
      var els = root.querySelectorAll('[data-lp-defer-src],[data-lp-defer-srcset]');
      if (!els.length) return;
      var done = false;
      function go() {
        if (done) return;
        done = true;
        for (var i = 0; i < els.length; i++) {
          var e = els[i];
          if (e.getAttribute('data-lp-defer-srcset')) e.srcset = e.getAttribute('data-lp-defer-srcset');
          if (e.getAttribute('data-lp-defer-src')) e.src = e.getAttribute('data-lp-defer-src');
        }
      }
      var hero = document.querySelector('img[fetchpriority="high"]');
      if (!hero || hero.complete) return go();
      hero.addEventListener('load', go, { once: true });
      hero.addEventListener('error', go, { once: true });
      window.addEventListener('scroll', go, { passive: true, once: true });
      window.addEventListener('touchstart', go, { passive: true, once: true });
      setTimeout(go, 4000);
    })(this);
  }

  connectedCallback() {
    this.cards = [...this.querySelectorAll('.lpsv__card:not(.lpsv__card--empty)')];
    if (!this.cards.length || this.ready) return;
    this.ready = true;
    this.deferPosters(); // LP-POSTER-DEFER
    this.previewMs = (parseInt(this.dataset.preview, 10) || 6) * 1000;
    this.autoplay = this.dataset.autoplay === 'true' && !SLOW && !REDUCED;
    this.visible = new Set();
    this.seen = new Set();
    this.current = null;
    this.muted = false;

    this.cards.forEach((card, i) => {
      card.querySelector('.lpsv__media').addEventListener('click', () => this.open(i));
      card.querySelector('.lpsv__prod').addEventListener('click', () => track('lp_video_click', this.data(i, 'tarjeta')));
    });

    this.io = new IntersectionObserver((entries) => this.onIntersect(entries), { threshold: [0, 0.6] });
    this.cards.forEach((c) => this.io.observe(c));
    this.onVisibility = () => (document.hidden ? this.stop() : this.next());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.rail = this.querySelector('.lpsv__track');
    this.prevBtn = this.querySelector('.lpsv__arrow--prev');
    this.nextBtn = this.querySelector('.lpsv__arrow--next');
    const step = () => (this.cards[0].getBoundingClientRect().width + 16) * 2;
    this.prevBtn.addEventListener('click', () => this.rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    this.nextBtn.addEventListener('click', () => this.rail.scrollBy({ left: step(), behavior: 'smooth' }));
    this.updateArrows = () => {
      const t = this.rail;
      const over = t.scrollWidth > t.clientWidth + 4;
      this.prevBtn.hidden = !over || t.scrollLeft < 8;
      this.nextBtn.hidden = !over || t.scrollLeft + t.clientWidth > t.scrollWidth - 8;
    };
    this.rail.addEventListener('scroll', this.updateArrows, { passive: true });
    this.ro = new ResizeObserver(this.updateArrows);
    this.ro.observe(this.rail);
    this.enableMouse();
  }

  // En PC: arrastrar con el mouse (en touch el scroll ya es nativo) y ← → con el teclado.
  enableMouse() {
    const rail = this.rail;
    let drag = null;
    let suppress = false;
    rail.addEventListener('dragstart', (e) => e.preventDefault());
    rail.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: rail.scrollLeft, id: e.pointerId, moved: false };
    });
    rail.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 6) {
        drag.moved = true;
        rail.classList.add('is-dragging');
        try { rail.setPointerCapture(drag.id); } catch (err) {}
      }
      if (drag.moved) rail.scrollLeft = drag.left - dx;
    });
    const end = () => {
      if (!drag) return;
      if (drag.moved) {
        suppress = true; // el click que sigue al arrastre no abre el video
        setTimeout(() => { suppress = false; }, 0);
        rail.classList.remove('is-dragging'); // vuelve el scroll-snap: se acomoda a la tarjeta más cercana
      }
      drag = null;
    };
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('click', (e) => {
      if (suppress) { e.preventDefault(); e.stopPropagation(); suppress = false; }
    }, true);

    rail.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const i = this.cards.findIndex((c) => c.contains(document.activeElement));
      const next = this.cards[i + (e.key === 'ArrowRight' ? 1 : -1)];
      if (i < 0 || !next) return;
      e.preventDefault();
      next.querySelector('.lpsv__media').focus();
      next.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  }

  disconnectedCallback() {
    this.stop();
    this.io?.disconnect();
    this.ro?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.viewer?.remove();
    this.ready = false;
  }

  data(i, origen) {
    const c = this.cards[i];
    return {
      sku: c.dataset.sku,
      titulo: c.dataset.titulo,
      video: c.dataset.video,
      posicion: i + 1,
      value: Number(c.dataset.precio) || 0,
      origen,
      bloque: this.dataset.bloque || 'home_videos', // la página de gracias Cyber lo marca 'cyber_gracias'
    };
  }

  // ---------- vista previa en la tarjeta ----------
  onIntersect(entries) {
    for (const e of entries) {
      const card = e.target;
      if (e.intersectionRatio >= 0.6) {
        this.visible.add(card);
        if (!this.seen.has(card)) {
          this.seen.add(card);
          track('lp_video_view', this.data(this.cards.indexOf(card), 'tarjeta'));
        }
      } else {
        this.visible.delete(card);
        if (card === this.current) this.stop();
      }
    }
    if (!this.current) this.next();
  }

  next() {
    if (!this.autoplay || this.viewerOpen || document.hidden) return;
    const list = this.cards.filter((c) => this.visible.has(c));
    if (!list.length) return;
    const at = this.current ? list.indexOf(this.current) : -1;
    this.play(list[(at + 1) % list.length]);
  }

  async play(card) {
    if (this.current && this.current !== card) this.pause(this.current);
    this.current = card;
    clearTimeout(this.timer);
    const v = card.querySelector('video');
    if (!v.getAttribute('src')) {
      const { low } = await renditions(v.dataset.hls);
      if (this.current !== card || this.viewerOpen) return;
      if (!low) { // sin versión liviana no se precarga nada: queda el póster
        this.timer = setTimeout(() => this.rotate(card), 50);
        return;
      }
      v.src = low;
    }
    v.muted = true;
    if (!v.dataset.bound) {
      v.dataset.bound = '1';
      v.addEventListener('playing', () => card.classList.add('is-playing'));
    }
    try { v.currentTime = 0; } catch (e) {}
    v.play()?.catch(() => {});
    this.timer = setTimeout(() => this.rotate(card), this.previewMs);
  }

  rotate(card) {
    if (card !== this.current) return;
    if (this.cards.filter((c) => this.visible.has(c)).length > 1) this.next();
    else this.timer = setTimeout(() => this.rotate(card), this.previewMs);
  }

  pause(card) {
    const v = card.querySelector('video');
    v.pause();
    card.classList.remove('is-playing');
  }

  stop() {
    clearTimeout(this.timer);
    if (this.current) this.pause(this.current);
    this.current = null;
  }

  // ---------- visor tipo historias ----------
  buildViewer() {
    const d = document.createElement('dialog');
    d.className = 'lpsv-viewer';
    d.setAttribute('aria-label', 'Videos');
    d.innerHTML = `
      <button type="button" class="lpsv-viewer__side lpsv-viewer__side--prev" data-act="prev" aria-label="Video anterior">${chevron('m15 6-6 6 6 6')}</button>
      <div class="lpsv-viewer__stage">
        <div class="lpsv-viewer__bars">${this.cards.map(() => '<span><i></i></span>').join('')}</div>
        <div class="lpsv-viewer__top">
          <button type="button" class="lpsv-viewer__btn" data-act="mute" aria-label="Silenciar">${ICON_SOUND}</button>
          <button type="button" class="lpsv-viewer__btn" data-act="close" aria-label="Cerrar">${ICON_CLOSE}</button>
        </div>
        <video class="lpsv-viewer__video" playsinline preload="auto"></video>
        <span class="lpsv-viewer__paused" aria-hidden="true">${ICON_PAUSE}</span>
        <button type="button" class="lpsv-viewer__zone lpsv-viewer__zone--prev" data-act="prev" aria-label="Video anterior"></button>
        <button type="button" class="lpsv-viewer__zone lpsv-viewer__zone--next" data-act="next" aria-label="Video siguiente"></button>
        <a class="lpsv-viewer__prod"></a>
      </div>
      <button type="button" class="lpsv-viewer__side lpsv-viewer__side--next" data-act="next" aria-label="Video siguiente">${chevron('m9 6 6 6-6 6')}</button>`;
    document.body.appendChild(d);

    const v = d.querySelector('video');
    this.vVideo = v;
    this.vBars = [...d.querySelectorAll('.lpsv-viewer__bars i')];
    this.vProd = d.querySelector('.lpsv-viewer__prod');
    this.vMute = d.querySelector('[data-act="mute"]');

    d.addEventListener('click', (e) => {
      const act = e.target.closest('[data-act]')?.dataset.act;
      if (act === 'close') return this.close();
      if (act === 'prev') return this.show(this.idx - 1);
      if (act === 'next') return this.show(this.idx + 1);
      if (act === 'mute') return this.setMuted(!this.muted);
      if (e.target === v) return this.togglePause();
      if (e.target === d) return this.close();
    });
    d.addEventListener('close', () => this.onClosed());
    d.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.show(this.idx + 1);
      if (e.key === 'ArrowLeft') this.show(this.idx - 1);
    });
    this.vProd.addEventListener('click', () => track('lp_video_click', this.data(this.idx, 'visor')));

    // gestos: deslizar abajo cierra, izquierda/derecha cambia de video
    let x0 = 0, y0 = 0;
    d.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
    d.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - x0;
      const dy = e.changedTouches[0].clientY - y0;
      if (dy > 90 && Math.abs(dx) < 60) this.close();
      else if (Math.abs(dx) > 60 && Math.abs(dy) < 60) this.show(this.idx + (dx < 0 ? 1 : -1));
    }, { passive: true });

    v.addEventListener('timeupdate', () => {
      if (v.duration) this.vBars[this.idx].style.transform = `scaleX(${v.currentTime / v.duration})`;
    });
    v.addEventListener('ended', () => this.show(this.idx + 1));
    v.addEventListener('error', () => {
      // si el HLS nativo o el 720p deducido fallan, cae al mp4 1080p que entrega Liquid
      const fb = this.cards[this.idx]?.querySelector('video').dataset.fallback;
      if (fb && v.getAttribute('src') !== fb) { v.src = fb; v.play()?.catch(() => {}); }
    });

    this.viewer = d;
    return d;
  }

  open(i) {
    this.stop();
    this.viewerOpen = true;
    const d = this.viewer || this.buildViewer();
    if (!d.open) d.showModal();
    document.documentElement.classList.add('lpsv-lock');
    this.show(i, true);
    track('lp_video_open', this.data(i, 'visor'));
  }

  show(i, first) {
    if (i < 0) i = 0;
    if (i >= this.cards.length) return this.close();
    if (i === this.idx && !first) return;
    this.idx = i;
    const card = this.cards[i];
    const src = card.querySelector('video').dataset;
    const v = this.vVideo;
    v.poster = card.querySelector('.lpsv__poster')?.currentSrc || '';
    v.muted = this.muted;
    const start = (url) => {
      if (this.idx !== i) return;
      v.src = url;
      v.play()?.catch(() => { v.muted = true; this.setMuted(true); v.play()?.catch(() => {}); });
    };
    // HLS nativo (iPhone/Safari): adaptativo, baja solo lo que se ve. Si no, mp4 720p.
    // En iOS el play() con sonido tiene que ir dentro del mismo toque: por eso HLS va sin await.
    if (src.hls && v.canPlayType('application/vnd.apple.mpegurl')) start(src.hls);
    else renditions(src.hls).then(({ mid }) => start(mid || src.fallback));
    this.viewer.classList.remove('is-paused');

    this.vBars.forEach((b, n) => { b.style.transform = n < i ? 'scaleX(1)' : 'scaleX(0)'; });
    const prod = card.querySelector('.lpsv__prod');
    this.vProd.href = prod.href;
    this.vProd.innerHTML = prod.innerHTML;
    const cta = this.vProd.querySelector('.lpsv__cta');
    if (cta && this.dataset.ctaViewer) cta.innerHTML = `${this.dataset.ctaViewer} <span aria-hidden="true">&rarr;</span>`;
    if (!first) track('lp_video_open', this.data(i, 'visor'));
  }

  togglePause() {
    const v = this.vVideo;
    if (v.paused) { v.play()?.catch(() => {}); this.viewer.classList.remove('is-paused'); }
    else { v.pause(); this.viewer.classList.add('is-paused'); }
  }

  setMuted(m) {
    this.muted = m;
    this.vVideo.muted = m;
    this.vMute.innerHTML = m ? ICON_MUTED : ICON_SOUND;
    this.vMute.setAttribute('aria-label', m ? 'Activar sonido' : 'Silenciar');
  }

  close() {
    if (this.viewer?.open) this.viewer.close();
  }

  onClosed() {
    const v = this.vVideo;
    v.pause();
    v.removeAttribute('src');
    v.load();
    this.idx = undefined;
    this.viewerOpen = false;
    document.documentElement.classList.remove('lpsv-lock');
    this.next();
  }
}

if (!customElements.get('lp-shoppable-videos')) customElements.define('lp-shoppable-videos', LpShoppableVideos);
